import React, { FC, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apollo } from "../../apollo";
import Grid from "@carrier-io/air-react/Grid";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import { Skeleton, Typography } from "@carrier-io/air-react";
import ButtonBase from "@carrier-io/air-react/ButtonBase";
import { UserStatusT } from "../../constants/user";
import { UserStatus } from "../../generated/ups/graphql";
import { StatusTag } from "../statusTag/StatusTag";
import { displayError } from "../../constants/error";
import { NotificationBgColor } from "../../types/notification";
import Notification from "../../components/notification/Notification";
import { useUpdateUser } from "../../hooks/useUpdateUser";
import { UIUserStatus, UserSearchParam } from "../../types/user";

type EditUserStatusProps = {
  status: UserStatusT;
  isLoading: boolean;
  refetchExistingUser: () => void;
};

export const EditUserStatus: FC<EditUserStatusProps> = ({
  status,
  isLoading,
  refetchExistingUser,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [isNotificationOpened, setIsNotificationOpened] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [notificationBgColor, setNotificationBgColor] = useState(
    NotificationBgColor.INFO,
  );
  const hideActivationButton = [
    UIUserStatus.PENDING,
    UIUserStatus.ACTIVE,
  ].includes(status as UIUserStatus);
  const showResendEmailButton = false;
  const username = searchParams.get(UserSearchParam.USERNAME);
  const region = searchParams.get(UserSearchParam.REGION);

  const [updateUser, { loading: loadingUpdate }] = useUpdateUser();

  const handleResendEmail = () => {
    setNotificationBgColor(NotificationBgColor.SUCCESS);
    setNotificationText("Email sent successfully");
    setIsNotificationOpened(true);
  };

  const handleActivateUser = async () => {
    if (!region || !username || hideActivationButton) {
      setNotificationBgColor(NotificationBgColor.WARNING);
      setNotificationText("Invalid data");
      setIsNotificationOpened(true);

      return;
    }

    const variables = {
      region,
      username,
      input: {
        status: UserStatus.ENABLED,
      },
    };

    try {
      await updateUser({ variables });

      refetchExistingUser();
      setNotificationBgColor(NotificationBgColor.INFO);
      setNotificationText("User activated successfully");
      setIsNotificationOpened(true);
    } catch (error) {
      setNotificationBgColor(NotificationBgColor.ERROR);
      setNotificationText(
        displayError((error as apollo.ApolloError).graphQLErrors[0]),
      );
      setIsNotificationOpened(true);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        paddingBottom: 3,
        marginBottom: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {isLoading ? (
        <Skeleton
          data-testid="status-loading-skeleton"
          height={127}
          width={300}
        />
      ) : (
        <Grid container spacing={2} columns={16}>
          <Grid size={8}>
            <StatusTag status={status} />
          </Grid>
          <Grid size={8}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              {showResendEmailButton && (
                <ButtonBase
                  data-testid="resend-email-button"
                  onClick={handleResendEmail}
                >
                  <Typography
                    color="primary"
                    variant="body1"
                    sx={{
                      textDecoration: "underline",
                    }}
                  >
                    Resend email invitation
                  </Typography>
                </ButtonBase>
              )}
              {!hideActivationButton && (
                <ButtonBase
                  data-testid="activate-user-button"
                  onClick={handleActivateUser}
                  disabled={hideActivationButton || loadingUpdate || isLoading}
                >
                  <Typography
                    color="primary"
                    variant="body1"
                    sx={{
                      textDecoration: "underline",
                    }}
                  >
                    Activate user
                  </Typography>
                </ButtonBase>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      <Notification
        isNotificationOpened={isNotificationOpened}
        textContent={notificationText}
        onCloseNotification={() => setIsNotificationOpened(false)}
        notificationBgColor={notificationBgColor}
      />
    </Box>
  );
};
